// for key handeling

import { Injectable, Inject  } from '@angular/core';
import { Functions, httpsCallableData } from '@angular/fire/functions';

import { Platform } from '@ionic/angular';

//import { Securekey } from 'secure-key-plugin';
import { IKeyStorage }  from '../crypto/key-manager';

// IndexedDB helper functions
const DB_NAME = 'crypto-db';
const STORE = 'keys';
const PRIV_KEY_ID = 'rsa-oaep-private';
const PUB_KEY_ID = 'rsa-oaep-public';
const WRAPPED_ID = 'wrappedS_v1';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T = unknown>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: any): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const req = store.put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function ab2b64(buf: ArrayBuffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b642ab(b64: string) {
  const bin = atob(b64);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u.buffer;
}

// Helpers
function b642bytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function hex2bytes(hex: string): Uint8Array {
  const clean = hex.replace(/\s+/g, '');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
}


function hexToUint8Array(hexString: string): Uint8Array {
  // Remove spaces, 0x prefix, etc.
  const cleanHex = hexString.replace(/[^0-9a-fA-F]/g, '');

  // console.log('cleanHex', cleanHex, cleanHex.length);

  if (cleanHex.length % 2 !== 0) {
    throw new Error('Hex string must have even length');
  }

  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let i = 0; i < cleanHex.length; i += 2) {
    bytes[i / 2] = parseInt(cleanHex.substring(i, i + 2), 16);
  }
  //console.log('bytes', bytes.length, bytes);
  return bytes;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64.trim());
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < out.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/[^0-9a-fA-F]/g, '');
  if (clean.length % 2 !== 0) throw new Error('Hex string must have even length');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
}

function normalizeS(plainBuf: ArrayBuffer): Uint8Array {
  const bytes = new Uint8Array(plainBuf);

      if (bytes.length === 32 || bytes.length === 64) {
        // Check if all bytes are ASCII printable (hex chars: 0-9, a-f, A-F)
        const isAsciiHex = bytes.every(
          (b) =>
            (b >= 48 && b <= 57) || // 0-9
            (b >= 97 && b <= 102) || // a-f
            (b >= 65 && b <= 70) // A-F
        );

        if (isAsciiHex) {
          // Use String.fromCharCode (more reliable than TextDecoder)
          let hexString = '';
          for (let i = 0; i < bytes.length; i++) {
            hexString += String.fromCharCode(bytes[i]);
          }

          // Clean any potential whitespace
          hexString = hexString.replace(/\s+/g, '');

          if (hexString.length === 32 || hexString.length === 64) {
            if (/^[0-9a-fA-F]+$/.test(hexString)) {
              return hexToUint8Array(hexString);
            }
          }
        }
      }

      // if Base64 string (22 or 44 chars for 16/32 bytes)
      if (bytes.length === 22 || bytes.length === 24 || bytes.length === 44 || bytes.length === 48) {
        try {
          let base64String = '';
          for (let i = 0; i < bytes.length; i++) {
            base64String += String.fromCharCode(bytes[i]);
          }

          // Check if valid base64
          if (/^[A-Za-z0-9+/=]+$/.test(base64String.trim())) {
            const decoded = b642bytes(base64String.trim());
            if (decoded.length === 16 || decoded.length === 32) {
              return decoded;
            }
          }
        } catch (e) {
          // Not base64, continue to next strategy
        }
      }

      // if  Hex string with potential encoding issues
      // Try to extract hex characters regardless of length
      try {
        let hexCandidate = '';
        for (let i = 0; i < bytes.length; i++) {
          const b = bytes[i];
          // Only take printable ASCII
          if (b >= 32 && b <= 126) {
            hexCandidate += String.fromCharCode(b);
          }
        }

        // Clean to only hex chars
        hexCandidate = hexCandidate.replace(/[^0-9a-fA-F]/g, '');

        if ((hexCandidate.length === 32 || hexCandidate.length === 64) && hexCandidate.length % 2 === 0) {
          return hexToUint8Array(hexCandidate);
        }
      } catch (e) {
        // Failed, continue
      }

      // format is unrecognized
      throw new Error(
        `Unable to parse key format. ` + `Byte length: ${bytes.length}. ` + `Please clear app data and re-provision.`
      );
    
  


/*
  // Case 1: already raw 16/32-byte key
  if (b.length === 16 || b.length === 32) return b;

  // Case 2: ASCII-hex text (only 0-9A-Fa-f plus whitespace)
  let isHexAscii = true,
    hexStr = '';
  for (let i = 0; i < b.length; i++) {
    const x = b[i];
    const isHex = (x >= 0x30 && x <= 0x39) || (x >= 0x41 && x <= 0x46) || (x >= 0x61 && x <= 0x66);
    const isWS = x === 0x20 || x === 0x0a || x === 0x0d || x === 0x09;
    if (isHex) hexStr += String.fromCharCode(x);
    else if (!isWS) {
      isHexAscii = false;
      break;
    }
  }
  if (isHexAscii) {
    const bytes = hexToBytes(hexStr);
    if (bytes.length === 16 || bytes.length === 32) return bytes;
  }

  // Case 3: Base64 text
  let b64 = '';
  let isB64 = true;
  for (let i = 0; i < b.length; i++) {
    const x = b[i];
    const ok =
      (x >= 0x41 && x <= 0x5a) ||
      (x >= 0x61 && x <= 0x7a) ||
      (x >= 0x30 && x <= 0x39) ||
      x === 0x2b ||
      x === 0x2f ||
      x === 0x3d ||
      x === 0x20 ||
      x === 0x0a ||
      x === 0x0d ||
      x === 0x09;
    if (!ok) {
      isB64 = false;
      break;
    }
    b64 += String.fromCharCode(x);
  }
  if (isB64) {
    b64 = b64.replace(/\s+/g, '');
    if (b64.length % 4 === 0) {
      const bytes = b64ToBytes(b64);
      if (bytes.length === 16 || bytes.length === 32) return bytes;
    }
  }

  throw new Error(`Unexpected S format/length: ${b.length} bytes after RSA-OAEP`);*/
  }

@Injectable({ providedIn: 'root' })
export class ProvisioningService {
  private privateKey: CryptoKey | null = null;
  private publicKey: CryptoKey | null = null;
  constructor(private fns: Functions, private platform: Platform,  @Inject('IKeyStorage') private keyStorage: IKeyStorage) {// console.log('🔍 ProvisioningService initialized');
   // console.log('   Platform:', this.getPlatformInfo());
  } // ✅ use the already-initialized Functions


     private getPlatformInfo(): string {
    if (this.platform.is('capacitor')) {
      return '📱 NATIVE (Capacitor)';
    } else if (this.platform.is('cordova')) {
      return '📱 NATIVE (Cordova)';
    } else {
      return '🌐 WEB';
    }
  }
  async ensureKeypairAndGetPubSpkiB64(): Promise<string> {

   
// Generate RSA key pair ( for web- uses Web Crypto API)
 // console.log('✅ Using WEB CRYPTO API');
  const storedPriv = await this.keyStorage.getPrivateKey();;
    const storedPub = await this.keyStorage.getPublicKey();

    if (storedPriv && storedPub) {
      this.privateKey = storedPriv;
      this.publicKey = storedPub;
      const spki = await crypto.subtle.exportKey('spki', storedPub);
      return ab2b64(spki);
    }

    const kp = await crypto.subtle.generateKey(
      { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
      true,
      ['encrypt', 'decrypt']
    );

     // Save private key (platform-specific storage)
    await this.keyStorage.savePrivateKey(kp.privateKey);
    await this.keyStorage.savePublicKey(kp.publicKey); 
     this.privateKey = kp.privateKey;
    this.publicKey = kp.publicKey;
   
    // Export public key as SPKI
    const spki = await crypto.subtle.exportKey('spki', kp.publicKey);

    return ab2b64(spki);


  }

  getPrivateKey(): CryptoKey | null {
    return this.privateKey;
  }

   getPublicKey():  CryptoKey | null {
  return this.publicKey
}

  async provisionIfNeeded(): Promise<void> {
    // already have wrappedkey - platform specefic?
   


    //console.log("provisionIfNeeded")

    const existing = await this.keyStorage.getWrappedKey();

   // const existing = await idbGet<string>(WRAPPED_ID);
    // console.log('existing', existing);
    if (existing) return;

    const pubSpkiB64 = await this.ensureKeypairAndGetPubSpkiB64();
    const fnName = 'provisionWrappedS'

    const call$ = httpsCallableData<{ pubSpkiB64: string }, { wrappedB64: string }>(
      this.fns,
      fnName
    )({ pubSpkiB64 });

   const data = await call$.toPromise();

    //platform specefic storage

    //await idbSet(WRAPPED_ID, data.wrappedB64);
     await this.keyStorage.saveWrappedKey(data.wrappedB64);
  }

  
  /************************************************************************************* */

  async unwrapS(): Promise<Uint8Array> {
      // Get wrapped key (platform-specific)
  //  const ctBuf = await idbGet<string>(WRAPPED_ID);

     const wrappedB64  = await this.keyStorage.getWrappedKey();

    if (!wrappedB64 ) {
      throw new Error('Wrapped key not found in IndexedDB. Please re-provision.');
    }

  
 // WEB: use WebCrypto RSA-OAEP
    if (!this.privateKey) {
      const priv = await this.keyStorage.getPrivateKey()
      if (!priv) throw new Error('Private key not found; re-provision.');
      this.privateKey = priv;
    }

    try {
      const ct = b642ab(wrappedB64 );
      const plainBuf = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, this.privateKey!, ct);

      const normalized = normalizeS(plainBuf);
        return normalized; 
    /*
     // const bytes = new Uint8Array(plainBuf);
     ////////////
      if (bytes.length === 32 || bytes.length === 64) {
        // Check if all bytes are ASCII printable (hex chars: 0-9, a-f, A-F)
        const isAsciiHex = bytes.every(
          (b) =>
            (b >= 48 && b <= 57) || // 0-9
            (b >= 97 && b <= 102) || // a-f
            (b >= 65 && b <= 70) // A-F
        );

        if (isAsciiHex) {
          // Use String.fromCharCode (more reliable than TextDecoder)
          let hexString = '';
          for (let i = 0; i < bytes.length; i++) {
            hexString += String.fromCharCode(bytes[i]);
          }

          // Clean any potential whitespace
          hexString = hexString.replace(/\s+/g, '');

          if (hexString.length === 32 || hexString.length === 64) {
            if (/^[0-9a-fA-F]+$/.test(hexString)) {
              return hexToUint8Array(hexString);
            }
          }
        }
      }

      // if Base64 string (22 or 44 chars for 16/32 bytes)
      if (bytes.length === 22 || bytes.length === 24 || bytes.length === 44 || bytes.length === 48) {
        try {
          let base64String = '';
          for (let i = 0; i < bytes.length; i++) {
            base64String += String.fromCharCode(bytes[i]);
          }

          // Check if valid base64
          if (/^[A-Za-z0-9+/=]+$/.test(base64String.trim())) {
            const decoded = b642bytes(base64String.trim());
            if (decoded.length === 16 || decoded.length === 32) {
              return decoded;
            }
          }
        } catch (e) {
          // Not base64, continue to next strategy
        }
      }

      // if  Hex string with potential encoding issues
      // Try to extract hex characters regardless of length
      try {
        let hexCandidate = '';
        for (let i = 0; i < bytes.length; i++) {
          const b = bytes[i];
          // Only take printable ASCII
          if (b >= 32 && b <= 126) {
            hexCandidate += String.fromCharCode(b);
          }
        }

        // Clean to only hex chars
        hexCandidate = hexCandidate.replace(/[^0-9a-fA-F]/g, '');

        if ((hexCandidate.length === 32 || hexCandidate.length === 64) && hexCandidate.length % 2 === 0) {
          return hexToUint8Array(hexCandidate);
        }
      } catch (e) {
        // Failed, continue
      }
/////////
      // If we got here, format is unrecognized
      throw new Error(
        `Unable to parse key format. ` + `Byte length: ${bytes.length}. ` + `Please clear app data and re-provision.`
      );*/
    } catch (error: any) {
      // Provide helpful error message
      if (error.message?.includes('Unable to parse key format')) {
        throw error;
      }

      throw new Error(
        `Failed to unwrap key: ${error.message}. ` + `This may indicate corrupted data. Please re-provision.`
      );
    }
  }

  async getAesKeyFromS(): Promise<CryptoKey> {
    try {
      const S = await this.unwrapS();
      const keyData = new Uint8Array(S);

      const key = await crypto.subtle.importKey('raw', keyData, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt']);

      return key;
    } catch (error: any) {
      console.error('Failed to get AES key:', error);

      // Check if it's a parsing error that requires re-provision
      if (error.message?.includes('Unable to parse') || error.message?.includes('re-provision')) {
        // Clear corrupted data
        await idbSet(WRAPPED_ID, null);

        // Notify user
        throw new Error('Encryption key data is corrupted. ' + 'Please reconnect to re-establish secure connection.');
      }

      throw error;
    }
  }

    async clearKeys(): Promise<void> {
    await this.keyStorage.clear();
  }
  
}
